import { createContext } from 'react';
import type { User, Organization } from '../types';
import type { SwitchOrganizationResponse } from '../lib/services/OrganizationService';

/**
 * Internal state shape — the pieces AuthProvider manages with useState.
 *
 * isAuthenticated is INTENTIONALLY not here: the provider derives it from
 * `isAuthenticatedState(sessionState) && !!user` (i.e. Alive || Refreshing)
 * so the value stays in lockstep with both the SessionManager state machine
 * and the user object. Refreshing counts as authenticated — see
 * isAuthenticatedState in lib/session/types.ts (CU-868kyhnm9). Mirrors the
 * pattern AdminAuthContext uses.
 */
interface AuthState {
  user: User | null;
  currentOrg: Organization | null;
  loading: boolean;
  error: Error | null;
}

interface AuthContextType extends AuthState {
  /** Derived: isAuthenticatedState(sessionState) && !!user (Alive || Refreshing). Not stored. */
  isAuthenticated: boolean;
  login: (email: string, password: string, organizationId?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
  setUser: (user: User | null) => void;
  /**
   * Apply a SwitchOrganizationResponse in-place: update user + currentOrg,
   * drive userSession to alive with the new expiry. Keeps the React tree
   * mounted so switching feels like a normal login transition instead of a
   * full page reload.
   */
  applySwitchedSession: (response: SwitchOrganizationResponse) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export type { AuthContextType, AuthState };
